/** Pure read helpers over game state / board. */
import { type CellId, HOME_CELLS, hexDistance, neighbors } from "../data/board";
import { HOME_ZONE_RADIUS } from "../data/constants";
import type { PlayerId } from "../types/common";
import type { AugmentedWarState, Board, Unit } from "../types/game-state";

export const opponentOf = (player: PlayerId): PlayerId =>
  player === "p1" ? "p2" : "p1";

/** Is the unit at `cell` engaged — adjacent to an enemy (Zone of Control, rule.md §6)? */
export function isEngaged(
  board: Board,
  cell: CellId,
  owner: PlayerId,
): boolean {
  return neighbors(cell).some((n) => {
    const u = board[n];
    return u !== undefined && u.owner !== owner;
  });
}

const ALL_HOME_CELLS: readonly CellId[] = [...HOME_CELLS.p1, ...HOME_CELLS.p2];

/** Is `cell` inside a home zone (within HOME_ZONE_RADIUS of any home)? ZoC only
 *  pins engaged units here; the neutral midfield is a free race (rule.md §6). */
export function inHomeZone(cell: CellId): boolean {
  return ALL_HOME_CELLS.some((h) => hexDistance(cell, h) <= HOME_ZONE_RADIUS);
}

export const getUnit = (board: Board, cell: CellId): Unit | undefined =>
  board[cell];

/** Cell ids occupied by `player`. */
export function cellsOwnedBy(
  board: Board,
  player: PlayerId,
): readonly CellId[] {
  return Object.keys(board).filter((cell) => board[cell]?.owner === player);
}

export const countUnitsOnBoard = (board: Board, player: PlayerId): number =>
  cellsOwnedBy(board, player).length;

/** Total HP a player has on the board (rule.md §4 tie-break). */
export function totalHpOnBoard(board: Board, player: PlayerId): number {
  let sum = 0;
  for (const cell of Object.keys(board)) {
    const unit = board[cell];
    if (unit?.owner === player) sum += unit.hp;
  }
  return sum;
}

/** A player still has pieces in play if any are on the board or in reserve. */
export function hasPiecesLeft(
  state: AugmentedWarState,
  player: PlayerId,
): boolean {
  return (
    countUnitsOnBoard(state.board, player) > 0 ||
    state.players[player].reserve > 0
  );
}
